import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import {
  Button,
  Spin,
} from 'antd';

import medsenderIcon from '../assets/img/redesign/medsender_icon.png';
import { resetAuth } from '../actions/auth';
import { captureAnalytics } from '../actions/preload';
import { validSendEmail } from '../global/validation';
import csrfHeaders from '../utils/csrfHeaders';
import {
  normalizeNpiToken,
  isValidNpiToken,
  normalizeFaxToken,
  formatFaxToken,
  formatFaxDisplay,
  isValidFaxToken,
} from '../utils/identifierTokens';

import './portalSignup.scss';

const parseFirstError = (errors) => {
  if (!errors || typeof errors !== 'object') return null;

  const firstKey = Object.keys(errors)[0];
  if (!firstKey) return null;

  const value = errors[firstKey];
  if (Array.isArray(value) && value.length > 0) return value[0];
  return null;
};

const splitCommaSeparated = (value) => value
  .split(',')
  .map((token) => token.trim())
  .filter(Boolean);

const MIN_PASSWORD_LENGTH = 12;

const PortalSignup = ({ location }) => {
  const token = useMemo(() => new URLSearchParams(location.search).get('token'), [location.search]);

  const [loadingToken, setLoadingToken] = useState(true);
  const [tokenError, setTokenError] = useState(null);
  const [tokenMeta, setTokenMeta] = useState(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');
  const [npis, setNpis] = useState([]);
  const [npiInput, setNpiInput] = useState('');
  const [faxNumbers, setFaxNumbers] = useState([]);
  const [faxInput, setFaxInput] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const [submitting, setSubmitting] = useState(false);
  // Keeps the button busy while the browser leaves for the portal.
  const [redirecting, setRedirecting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [signupComplete, setSignupComplete] = useState(false);
  const [signupPendingApproval, setSignupPendingApproval] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('Missing invitation token');
        setLoadingToken(false);
        return;
      }

      try {
        const response = await axios.get('/api/v1/external_partner_invitations/validate_token', {
          params: { token },
        });

        if (!response.data.valid) {
          setTokenError(response.data.error || 'Invitation is no longer valid');
          setLoadingToken(false);
          return;
        }

        setTokenMeta(response.data);
        setEmail(response.data.email || '');
        setOrgName(response.data.company_name || '');
      } catch {
        setTokenError('Unable to validate invitation');
      } finally {
        setLoadingToken(false);
      }
    };

    validateToken();
  }, [token]);

  const onSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await axios.post('/api/v1/external_partner_registrations', {
        registration: {
          invitation_token: token,
          email: email.trim(),
          password,
          password_confirmation: passwordConfirmation,
          name: `${firstName} ${lastName}`.trim(),
          org_name: orgName.trim(),
          npis,
          fax_numbers: faxNumbers,
        },
      }, { headers: csrfHeaders() }); // the server signs in only with a valid CSRF token

      const signedIn = response.data?.signed_in === true;
      captureAnalytics('portal_signup_completed', {
        signed_in: signedIn,
        pending_approval: response.data?.pending_approval !== false,
      });

      // The server signs the partner in when the invitation proved their email.
      // Full page load so the app boots with the new session; drop any cached
      // data from an earlier user of this browser first.
      if (signedIn) {
        setRedirecting(true);
        resetAuth();
        window.location.assign(response.data.redirect_path || '/app/referral_portal');
        return;
      }

      setSignupPendingApproval(response.data?.pending_approval !== false);
      setSignupComplete(true);
    } catch (error) {
      setSubmitError(error.response?.data?.error || parseFirstError(error.response?.data?.errors) || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  const passwordValid = password.length >= MIN_PASSWORD_LENGTH
    && /[A-Z]/.test(password)
    && /\d/.test(password);

  const passwordsMatch = password === passwordConfirmation;

  // When the invitation was sent without an email (e.g., a fax invitation
  // where the inviter didn't know the partner's address), the partner provides
  // their own email at signup. Otherwise the field is locked to the invited
  // email so the partner can't sidestep the strict-match check.
  const emailLockedToInvitation = !!tokenMeta?.email;
  const emailValid = emailLockedToInvitation ? true : validSendEmail(email.trim());

  const canSubmit = email.trim()
    && emailValid
    && firstName.trim()
    && lastName.trim()
    && orgName.trim()
    && passwordValid
    && passwordsMatch
    && npis.length > 0
    && faxNumbers.length > 0;

  const addNpiToken = (rawValue) => {
    const normalized = normalizeNpiToken(rawValue);
    if (!isValidNpiToken(normalized)) return;

    setNpis((current) => Array.from(new Set([...current, normalized])));
  };

  const addFaxToken = (rawValue) => {
    const normalized = normalizeFaxToken(rawValue);
    if (!isValidFaxToken(normalized)) return;

    setFaxNumbers((current) => Array.from(new Set([...current, normalized])));
  };

  const commitNpiInput = () => {
    addNpiToken(npiInput);
    setNpiInput('');
  };

  const commitFaxInput = () => {
    addFaxToken(faxInput);
    setFaxInput('');
  };

  const onNpiInputChange = (event) => {
    setNpiInput(normalizeNpiToken(event.target.value));
  };

  const onFaxInputChange = (event) => {
    setFaxInput(normalizeFaxToken(event.target.value));
  };

  const onNpiInputKeyDown = (event) => {
    if (event.key === ',' || event.key === 'Enter') {
      event.preventDefault();
      commitNpiInput();
      return;
    }

    if (event.key === 'Backspace' && npiInput.length === 0) {
      setNpis((current) => current.slice(0, -1));
    }
  };

  const onFaxInputKeyDown = (event) => {
    if (event.key === ',' || event.key === 'Enter') {
      event.preventDefault();
      commitFaxInput();
      return;
    }

    if (event.key === 'Backspace' && faxInput.length === 0) {
      setFaxNumbers((current) => current.slice(0, -1));
    }
  };

  const onNpiPaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text') || '';
    splitCommaSeparated(pasted).forEach((value) => addNpiToken(value));
  };

  const onFaxPaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text') || '';
    splitCommaSeparated(pasted).forEach((value) => addFaxToken(value));
  };

  const removeNpi = (value) => {
    setNpis((current) => current.filter((item) => item !== value));
  };

  const removeFax = (value) => {
    setFaxNumbers((current) => current.filter((item) => item !== value));
  };

  if (loadingToken) {
    return (
      <div className="portal-signup">
        <div className="portal-signup__card portal-signup__card--state">
          <Spin />
          <span>Validating invitation...</span>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="portal-signup">
        <div className="portal-signup__card portal-signup__card--state">
          <img src={medsenderIcon} alt="Medsender" className="portal-signup__logo" />
          <h1 className="portal-signup__title">Join the Referral Portal</h1>
          <p className="portal-signup__error">{tokenError}</p>
          <Button href="/login">Go to sign in</Button>
        </div>
      </div>
    );
  }

  if (signupComplete) {
    return (
      <div className="portal-signup">
        <div className="portal-signup__card portal-signup__card--state">
          <img src={medsenderIcon} alt="Medsender" className="portal-signup__logo" />
          <h1 className="portal-signup__title portal-signup__title--success">Account Created</h1>
          {signupPendingApproval ? (
            <p className="portal-signup__success-message">
              Your account has been created successfully. It is now pending approval
              from
              {' '}
              <strong>{tokenMeta?.inviting_organization}</strong>
              . You will receive an email once your account has been approved.
            </p>
          ) : (
            <p className="portal-signup__success-message">
              Your account has been created successfully. You can now sign in to
              access the referral portal for
              {' '}
              <strong>{tokenMeta?.inviting_organization}</strong>
              .
            </p>
          )}
          <Button href="/login" type="primary" className="portal-signup__success-btn">
            Go to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-signup">
      <div className="portal-signup__card">
        <img src={medsenderIcon} alt="Medsender" className="portal-signup__logo" />

        <h1 className="portal-signup__title">Join the Referral Portal</h1>

        <p className="portal-signup__subtitle">
          Invited by
          {' '}
          {tokenMeta?.inviting_organization}
        </p>

        <div className="portal-signup__grid portal-signup__grid--two-col">
          <div className="portal-signup__field">
            <span className="portal-signup__field-label">First Name*</span>
            <input
              id="portal-first-name"
              type="text"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
          </div>

          <div className="portal-signup__field">
            <span className="portal-signup__field-label">Last Name*</span>
            <input
              id="portal-last-name"
              type="text"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </div>
        </div>

        <div className="portal-signup__field">
          <span className="portal-signup__field-label">Email*</span>
          <input
            id="portal-email"
            type="email"
            value={email}
            readOnly={emailLockedToInvitation}
            onChange={emailLockedToInvitation ? undefined : (event) => setEmail(event.target.value)}
            className={emailLockedToInvitation ? 'portal-signup__readonly' : undefined}
            placeholder={emailLockedToInvitation ? undefined : 'you@example.com'}
          />
        </div>

        <div className="portal-signup__field">
          <span className="portal-signup__field-label">Organization Name*</span>
          <input
            id="portal-org"
            type="text"
            value={orgName}
            onChange={(event) => setOrgName(event.target.value)}
          />
        </div>

        <div className="portal-signup__field">
          <span className="portal-signup__field-label">NPI Number(s)*</span>
          <div className="portal-signup__tags portal-signup__tags--npi">
            <div className="portal-signup__chip-list">
              {npis.map((value) => (
                <span key={value} className="portal-signup__tag">
                  <span>{value}</span>
                  <button
                    type="button"
                    className="portal-signup__tag-close"
                    onClick={() => removeNpi(value)}
                    aria-label={`Remove ${value}`}
                  >
                    ×
                  </button>
                </span>
              ))}

              <input
                id="portal-npi"
                type="text"
                className="portal-signup__chip-input"
                value={npiInput}
                onChange={onNpiInputChange}
                onKeyDown={onNpiInputKeyDown}
                onBlur={commitNpiInput}
                onPaste={onNpiPaste}
                placeholder={npis.length === 0 ? 'Enter your NPI(s), separated by comma' : ''}
              />
            </div>
          </div>
        </div>

        <div className="portal-signup__field">
          <span className="portal-signup__field-label">Fax Number(s)*</span>
          <div className="portal-signup__tags portal-signup__tags--fax">
            <div className="portal-signup__chip-list">
              {faxNumbers.map((value) => (
                <span key={value} className="portal-signup__tag portal-signup__tag--fax">
                  <span>{formatFaxDisplay(value)}</span>
                  <button
                    type="button"
                    className="portal-signup__tag-close"
                    onClick={() => removeFax(value)}
                    aria-label={`Remove ${formatFaxDisplay(value)}`}
                  >
                    ×
                  </button>
                </span>
              ))}

              <input
                id="portal-fax"
                type="text"
                className="portal-signup__chip-input"
                value={formatFaxToken(faxInput)}
                onChange={onFaxInputChange}
                onKeyDown={onFaxInputKeyDown}
                onBlur={commitFaxInput}
                onPaste={onFaxPaste}
                placeholder={faxNumbers.length === 0 ? 'Enter your fax number(s), separated by comma' : ''}
              />
            </div>
          </div>
        </div>

        <div className="portal-signup__field">
          <span className="portal-signup__field-label">Password*</span>
          <input
            id="portal-password"
            type="password"
            value={password}
            minLength={MIN_PASSWORD_LENGTH}
            onChange={(event) => setPassword(event.target.value)}
          />
          <span className="portal-signup__field-hint">
            Password must be at least 12 characters, with at least one uppercase letter and one digit.
          </span>
        </div>

        <div className="portal-signup__field">
          <span className="portal-signup__field-label">Confirm Password*</span>
          <input
            id="portal-password-confirm"
            type="password"
            value={passwordConfirmation}
            minLength={MIN_PASSWORD_LENGTH}
            onChange={(event) => setPasswordConfirmation(event.target.value)}
          />
        </div>

        {submitError && (
          <p className="portal-signup__error">{submitError}</p>
        )}

        <Button
          type="primary"
          onClick={onSubmit}
          loading={submitting || redirecting}
          disabled={!canSubmit}
          className="portal-signup__submit"
        >
          Create Account
        </Button>

        <p className="portal-signup__footer">
          Already have an account?
          <a href="/login">Log in</a>
        </p>
      </div>
    </div>
  );
};

PortalSignup.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

export default PortalSignup;
